import React, { useState, useRef, useEffect } from "react";
import scsLogo from "../images/scs.webp";
import accLogo from "../images/access_logo_white_with_tagline_cropped.png";

// Styles remain the same but add new styles for the organization error message
const styles = `
  /* Previous styles remain the same */
  .organization-error {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background-color: #fff;
    padding: 2rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    text-align: center;
  }
  
  .organization-error h2 {
    color: #131736;
    margin-bottom: 1rem;
  }
  
  .organization-error p {
    color: #4a5568;
    margin-bottom: 1.5rem;
  }

  .organization-error-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }
  
   /* General focus styles for interactive elements */
  a:focus:not(.skip-link),
  button:focus,
  input:focus,
  select:focus,
  textarea:focus {
    outline: 2px solid #007bff;
    outline-offset: 2px;
  }

  @media (forced-colors: active) {
    a:focus:not(.skip-link),
    button:focus,
    input:focus,
    select:focus,
    textarea:focus {
      outline: 3px solid HighlightText;
    }
  }

  /* Styles for skip links - kept invisible even when focused */
  .skip-link {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }

  .skip-link:focus {
    outline: none;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }

  .form-group {
    margin-bottom: 1rem;
  }
  .error-message {
    color: #dc3545;
    font-weight: bold;
  }
`;

const MessageComponent = () => {
  const [caregiverIdentifier, setCaregiverIdentifier] = useState("");
  const [error, setError] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [organizationIdentifier, setOrganizationIdentifier] = useState("");
  const [organizationLogo, setOrganizationLogo] = useState("");
  const [hasValidOrganization, setHasValidOrganization] = useState(false);
  const inputRef = useRef(null);
  
  const IMAGES = {
    'SCS': scsLogo,
    'ACC': accLogo
  }


  useEffect(() => {
    // Get the organization identifier from the URL path on component mount
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const orgId = pathParts[pathParts.length - 1] || '';
    
    if (orgId) {
      setOrganizationIdentifier(orgId.toUpperCase());
      setHasValidOrganization(true);
      setOrganizationLogo(IMAGES[orgId.toUpperCase()])
    } else {
      setHasValidOrganization(false);
    }
    console.log('logo', IMAGES[orgId.toUpperCase()]);
  }, []);

  const validateIdentifier = (value) => {
    const pattern = new RegExp(`^${organizationIdentifier}\\d+$`);
    return pattern.test(value);
  };

  const handleInputChange = (e) => {
    const value = e.target.value.toUpperCase();
    setCaregiverIdentifier(value);
    
    if (isDirty) {
      if (value === "") {
        setError("Please enter an identifier");
      } else if (!validateIdentifier(value)) {
        setError(`Identifier must start with ${organizationIdentifier} followed by a number (e.g., ${organizationIdentifier}99)`);
      } else {
        setError("");
      }
    }
  };

  const handleBlur = () => {
    setIsDirty(true);
    if (caregiverIdentifier === "") {
      setError("Please enter an identifier");
    } else if (!validateIdentifier(caregiverIdentifier)) {
      setError(`Identifier must start with ${organizationIdentifier} followed by digits (e.g., ${organizationIdentifier}99)`);
    } else {
      setError("");
    }
  };


  const REDIRECT_URLS = {
    'SCS': 'https://www.archangels-cii.me/anycarecounts-mi/?utm_campaign=accmi&utm_term=tpi-exhale&utm_pc=cic&',
    'ACC': 'https://www.archangels-cii.me/anycarecounts-mi/?utm_source=access&utm_campaign=accmi&utm_term=tpi-exhale&utm_pc=cic&'
  };


// Then modify handleSubmit:
const handleSubmit = (e) => {
  e.preventDefault();
  if (!caregiverIdentifier || !validateIdentifier(caregiverIdentifier)) {
    setError(`Please enter a valid identifier in the format ${organizationIdentifier} followed by 3 digits`);
    return;
  }

  const linkIdentifier = caregiverIdentifier.replace(organizationIdentifier, 'link-');
  const currentUrl = new URL(window.location.href);
  const params = new URLSearchParams(currentUrl.search);
  
  params.append("utm_medium", linkIdentifier);
  
  const baseUrl = REDIRECT_URLS[organizationIdentifier] ; // Default to SCS
  window.location.href = `${baseUrl}${params.toString()}`;
};


  // If no organization identifier is present, show the error message
  if (!hasValidOrganization) {
    return (
      <>
        <style>{styles}</style>
        <div className="organization-error-overlay" />
        <div className="organization-error" role="alert" aria-live="assertive">
          <h2>Organization Identifier Required</h2>
          <p>
            Please access this page using your organization's specific URL.
            The URL should include your organization identifier, for example:
          </p>
          <p className="font-mono text-sm mb-4">
            https://exhale.archangels-data.me/<strong>YOUR-ORGANIZATION-ID</strong>
          </p>
          <p className="font-mono text-sm mb-4">
            SCS: https://exhale.archangels-data.me/SCS<br/>
            ACCESS: https://exhale.archangels-data.me/ACC<br/>
          </p>
          <p>
            If you're unsure about your organization's URL, please contact your administrator
            or the support team for assistance.
          </p>
        </div>
      </>
    );
  }

  // Regular component render
  return (
    <>
      <style>{styles}</style>
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <a href="#caregiver-form" className="skip-link">Skip to form</a>

      <main id="main-content" role="main" aria-label="Caregiver Assessment Form" className="container mt-5 background-custom h-100">
        <div className="px-lg-5 px-md-2 px-sm-1">
          <header role="banner" className="text-center mb-4">
            <img
              src={organizationLogo}
              alt={`${organizationIdentifier} Caregiving Logo`}
              className="img-fluid"
              style={{ maxWidth: '200px' }}
            />
          </header>

          <h1 className="custom-heading text-center">Complete the Caregiver Intensity Index</h1>
          <form id="caregiver-form" onSubmit={handleSubmit} className="mt-4" noValidate>
            <div className="form-group d-flex flex-column align-items-center">
              <label htmlFor="caregiverIdentifier" className="sr-only">
                Enter the NYAM ID for this caregiver in the format {organizationIdentifier} followed by digits (e.g., {organizationIdentifier}99)
              </label>
              <input
                type="text"
                className={`form-control ${isDirty && error ? 'is-invalid' : ''}`}
                id="caregiverIdentifier"
                name="caregiverIdentifier"
                ref={inputRef}
                value={caregiverIdentifier}
                required
                aria-required="true"
                aria-invalid={error ? "true" : "false"}
                aria-describedby="caregiverIdentifierHelp errorMessage"
                placeholder={`Enter ID (e.g., ${organizationIdentifier}99)`}
                maxLength={organizationIdentifier.length + 3} 
                style={{
                  backgroundColor: '#f0f0f0',
                  maxWidth: '300px',
                  width: '100%'
                }}
                onChange={handleInputChange}
                onBlur={handleBlur}
              />
              <div id="caregiverIdentifierHelp" className="form-text mt-1">
                Format: {organizationIdentifier} followed by digits (e.g., {organizationIdentifier}99)
              </div>
              {isDirty && error && (
                <div
                  id="errorMessage"
                  className="error-message mt-2 text-sm"
                  role="alert"
                >
                  {error}
                </div>
              )}
            </div>
            <div className="text-center mt-3">
              <button
                type="submit"
                className="btn btn-lg"
                style={{
                  fontSize: "15px",
                  width: "170px",
                  height: "45px",
                  backgroundColor: "#131736",
                  color: "white",
                  border: "none"
                }}
                disabled={!caregiverIdentifier || !validateIdentifier(caregiverIdentifier)}
                aria-disabled={!caregiverIdentifier || !validateIdentifier(caregiverIdentifier)}
              >
                NEXT
              </button>
            </div>
          </form>
        </div>
      </main>

      <footer role="contentinfo" aria-label="Page footer" className="mt-5">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <p className="custom-heading text-center">
                Thank you for your support in helping all caregivers get connected to resources based on their unique needs.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default MessageComponent;

